import { useEffect, useMemo } from "react";
import {
  PackageUnitTypeEnum,
  type PackageServiceTypeEnum,
} from "@/__generated__/graphql";
import { useBookingRouterInterceptor } from "@/components/hooks/use-booking-route";
import { WebBackButtonHeader } from "@/components/shared/back-button";
import { View } from "@/components/ui";
import { Column, Container } from "@/components/ui/layout";
import { getDepartment } from "@/lib/booking-lib";
import { capitalizeFirstLetter } from "@/lib/helpers/string";
import { PackageServiceTypeMapping } from "@/lib/service/helpers";
import { getPlatform } from "@/lib/utils";
import { useBookingState } from "@/store/booking/useBookingState";
import type { ServiceName, UpholsteryServiceName } from "@/types/service";
import { UpholsteryServiceNames } from "@/types/service";

import { BookingNavigation } from "./booking-navigation";
import { AirconBooking } from "./services/aircon-booking";
// import { BabySitterBooking } from "./services/babysitter";
import { DeepCleaningBooking } from "./services/deep-cleaning-booking";
import { ElderCareBooking } from "./services/elder-care-booking";
import { HomeBeautyBooking } from "./services/home-beauty-booking";
import { HomeCleaningBooking } from "./services/home-cleaning-booking";
import { HomeMassageBooking } from "./services/home-massage-booking";
import { LashBooking } from "./services/lash-booking";
import { PetGroomingBooking } from "./services/pet-grooming-booking";
import { PetSittingBooking } from "./services/pet-sitting-booking";
import { UpholsteryCleaningBooking } from "./services/upholstery-cleaning-booking";

const ServiceBooking = ({ name }: { name: ServiceName }) => {
  if (UpholsteryServiceNames.includes(name as UpholsteryServiceName)) {
    return <UpholsteryCleaningBooking name={name as UpholsteryServiceName} />;
  }

  switch (name) {
    case "home-cleaning":
      return <HomeCleaningBooking />;
    case "aircon-service":
      return <AirconBooking />;
    case "home-beauty":
      return <HomeBeautyBooking />;
    case "home-massage":
      return <HomeMassageBooking />;
    case "pet-grooming":
      return <PetGroomingBooking />;
    case "pet-sitting":
      return <PetSittingBooking />;
    case "lashes":
      return <LashBooking />;
    case "deep-cleaning":
      return <DeepCleaningBooking />;
    case "elder-care":
      return <ElderCareBooking />;
    // still un hold pending on vertical lead babysitter
    // case "babysitter":
    //   return <BabySitterBooking />;
  }
};

export function Booking({ name }: { name: ServiceName }) {
  useBookingRouterInterceptor();
  const platform = getPlatform();
  const {
    data: { packageDetailsFilter },
    setBookingState,
  } = useBookingState();

  useEffect(() => {
    setBookingState({
      department: getDepartment(name),
      isRebook: false,
      additionalNotes: "",
    });
  }, []);

  const backButtonTitle = useMemo(() => {
    if (name === "pet-grooming") {
      return packageDetailsFilter.unitTypes?.[0] === PackageUnitTypeEnum.Dog
        ? "Dog Grooming"
        : "Cat Grooming";
    }
    if (name === "home-beauty") {
      return "Manicure Pedicure";
    }

    if (name === "pram-seat") {
      return "Pram & Car Seat";
    }

    if (name === "aircon-service")
      return (
        "Aircon " +
        (PackageServiceTypeMapping?.[
          packageDetailsFilter
            ?.serviceTypes?.[0] as unknown as PackageServiceTypeEnum
        ] || "")
      );
    return name
      .split("-")
      .map((str) => capitalizeFirstLetter(str))
      .join(" ");
  }, [name]);

  return (
    <Container>
      <Column desktop="8">
        <WebBackButtonHeader title={backButtonTitle} />
        <View className="mb-24">
          <ServiceBooking name={name} />
        </View>
      </Column>
      <Column desktop="4">
        {platform === "web" && (
          <View className="py-4 md:sticky md:top-14">
            <BookingNavigation name={name} />
          </View>
        )}
      </Column>
    </Container>
  );
}
